import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../hooks/useAuthStore'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { getErrorMessage } from '../utils/errorHandler'

export const LineCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { login } = useAuthStore()
  const [error, setError] = useState<string | null>(null)
  
  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code')
      const state = searchParams.get('state')
      const error = searchParams.get('error')
      
      // Handle OAuth errors
      if (error) {
        setError(`LINE OAuth error: ${error}`)
        setTimeout(() => navigate('/login'), 3000)
        return
      }
      
      // Handle missing authorization code
      if (!code) {
        setError('Authorization code not found')
        setTimeout(() => navigate('/login'), 3000)
        return
      }
      
      try {
        // Exchange code for JWT token via backend
        const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787/api'
        const response = await fetch(`${API_BASE_URL}/auth/line/callback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ code, state })
        })
        
        const data = await response.json()
        
        if (data.success && data.data.token && data.data.user) {
          // Store authentication data
          login(data.data.user, data.data.token)
          
          // Redirect based on user type
          if (data.data.user.type === 'admin') {
            navigate('/admin/dashboard')
          } else {
            navigate('/dashboard')
          }
        } else {
          throw new Error(data.message || 'Authentication failed')
        }
      } catch (err) {
        console.error('LINE OAuth callback error:', err)
        setError(getErrorMessage(err))
        setTimeout(() => navigate('/login'), 3000)
      }
    }
    
    handleCallback()
  }, [searchParams, navigate, login])
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <div className="text-red-600 text-6xl mb-4">❌</div>
            <h1 className="text-xl font-bold text-red-800 mb-2">เข้าสู่ระบบไม่สำเร็จ</h1>
            <p className="text-red-600 text-sm mb-4">{error}</p>
            <p className="text-gray-500 text-xs">กำลังเปลี่ยนเส้นทางไปหน้าเข้าสู่ระบบ...</p>
          </div>
        </div>
      </div>
    )
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-600">กำลังดำเนินการเข้าสู่ระบบ...</p>
        <p className="mt-2 text-sm text-gray-500">โปรดรอสักครู่</p>
      </div>
    </div>
  )
}